import { useMemo, useState } from 'react';
import {
  ActionIcon,
  Badge,
  Button,
  Group,
  Menu,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useDebouncedValue } from '@mantine/hooks';
import {
  IconDownload,
  IconFileSpreadsheet,
  IconFileTypePdf,
  IconPlus,
  IconSearch,
  IconSignature,
} from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';

import { api, downloadFile, unwrap } from '../../api/client';
import type { FlightRecordList, LogbookScopeKind, LogbookSummary } from '../../api/types';
import { useSession } from '../../app/session';
import { DataTable } from '../../components/DataTable';
import { StatusBadge } from '../../components/StatusBadge';
import { formatDate, formatUtcTime, minutesToHhmm, toApiDate } from '../../lib/format';
import { notifyError } from '../../lib/notify';
import { FlightDrawer } from './FlightDrawer';
import { purposeLabel } from './labels';

type Props = {
  kind: LogbookScopeKind;
  subjectId: number;
  /** Preselects the aircraft when filing from an aircraft's own logbook. */
  aircraftId?: number;
  personId?: number;
  title?: string;
};

/**
 * The logbook grid, used by all five views.
 *
 * The column set follows the AMC1 FCL.050 page: identification of the flight, then the times
 * broken down by pilot function, then the operational columns. Gliding swaps the single/multi
 * engine columns for launches, which is how a glider book is actually printed.
 */
export function LogbookView({ kind, subjectId, aircraftId, personId, title }: Props) {
  const { t } = useTranslation();
  const { can, enumChoices, enumLabel } = useSession();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [dateFrom, setDateFrom] = useState<Date | null>(null);
  const [dateTo, setDateTo] = useState<Date | null>(null);
  const [purpose, setPurpose] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebouncedValue(search, 250);
  const [onlyUncertified, setOnlyUncertified] = useState(false);

  const [flightId, setFlightId] = useState<number | null>(null);
  const [isDrawerOpen, setDrawerOpen] = useState(false);

  const query = useMemo(
    () => ({
      page,
      page_size: pageSize,
      date_from: toApiDate(dateFrom) ?? undefined,
      date_to: toApiDate(dateTo) ?? undefined,
      purpose: purpose ?? undefined,
      search: debouncedSearch || undefined,
      uncertified: onlyUncertified ? true : undefined,
    }),
    [page, pageSize, dateFrom, dateTo, purpose, debouncedSearch, onlyUncertified],
  );

  const logbook = useQuery({
    queryKey: ['logbook', kind, subjectId, query],
    queryFn: async () =>
      unwrap(
        await api.GET('/api/v1/logbook/view/{kind}/{subject_id}/', {
          params: { path: { kind, subject_id: subjectId }, query },
        }),
      ),
  });

  const isGliding = kind === 'AIRCRAFT' && (logbook.data?.results ?? []).some((row) => row.launches);
  const showMyColumns = kind === 'PILOT';

  const columns = useMemo<ColumnDef<FlightRecordList, unknown>[]>(() => {
    const base: ColumnDef<FlightRecordList, unknown>[] = [
      {
        accessorKey: 'date',
        header: t('logbook.date'),
        meta: { width: 96, mono: true },
        cell: ({ row }) => formatDate(row.original.date),
      },
      {
        id: 'departure',
        header: t('logbook.departurePlace'),
        meta: { width: 120 },
        cell: ({ row }) => (
          <Group gap={6} wrap="nowrap">
            <Text fz="sm" fw={600} className="identifier">
              {row.original.departure_place}
            </Text>
            <Text fz="xs" c="dimmed" ff="monospace">
              {formatUtcTime(row.original.departure_time)}
            </Text>
          </Group>
        ),
      },
      {
        id: 'arrival',
        header: t('logbook.arrivalPlace'),
        meta: { width: 120 },
        cell: ({ row }) => (
          <Group gap={6} wrap="nowrap">
            <Text fz="sm" fw={600} className="identifier">
              {row.original.arrival_place}
            </Text>
            <Text fz="xs" c="dimmed" ff="monospace">
              {formatUtcTime(row.original.arrival_time)}
            </Text>
          </Group>
        ),
      },
    ];

    if (kind !== 'AIRCRAFT') {
      base.push({
        id: 'aircraft',
        header: t('logbook.aircraft'),
        meta: { width: 150 },
        cell: ({ row }) => (
          <Stack gap={0}>
            <Text fz="sm" fw={600} className="identifier">
              {row.original.registration}
            </Text>
            <Text fz="xs" c="dimmed">
              {row.original.type_label}
            </Text>
          </Stack>
        ),
      });
    }

    base.push({
      id: 'pic',
      header: t('logbook.picName'),
      meta: { width: 160 },
      cell: ({ row }) => (
        <Text fz="sm" className="pic-name">
          {row.original.pic_name}
        </Text>
      ),
    });

    if (!isGliding) {
      base.push(
        {
          accessorKey: 'single_pilot_se_time',
          header: t('logbook.se'),
          meta: { width: 62, align: 'right', mono: true },
          cell: ({ row }) => nonZero(row.original.single_pilot_se_time),
        },
        {
          accessorKey: 'single_pilot_me_time',
          header: t('logbook.me'),
          meta: { width: 62, align: 'right', mono: true },
          cell: ({ row }) => nonZero(row.original.single_pilot_me_time),
        },
        {
          accessorKey: 'multi_pilot_time',
          header: t('logbook.mp'),
          meta: { width: 62, align: 'right', mono: true },
          cell: ({ row }) => nonZero(row.original.multi_pilot_time),
        },
      );
    }

    base.push({
      accessorKey: 'total_time',
      header: t('logbook.totalTime'),
      meta: { width: 72, align: 'right', mono: true },
      cell: ({ row }) => (
        <Text fz="sm" fw={600} ff="monospace">
          {row.original.total_time}
        </Text>
      ),
    });

    if (isGliding) {
      base.push({
        id: 'launches',
        header: t('logbook.launches'),
        meta: { width: 90, align: 'right', mono: true },
        cell: ({ row }) => (
          <Group gap={4} justify="flex-end" wrap="nowrap">
            <Text fz="sm" ff="monospace">
              {row.original.launches || ''}
            </Text>
            {row.original.launch_method ? (
              <Text fz="xs" c="dimmed">
                {enumLabel('launch_method', row.original.launch_method)}
              </Text>
            ) : null}
          </Group>
        ),
      });
    } else {
      base.push({
        id: 'landings',
        header: t('logbook.landings'),
        meta: { width: 80, align: 'right', mono: true },
        cell: ({ row }) => (
          <Text fz="sm" ff="monospace">
            {row.original.landings_day || 0}
            {row.original.landings_night ? (
              <Text span c="blue.5" fz="sm">
                {' '}
                +{row.original.landings_night}
              </Text>
            ) : null}
          </Text>
        ),
      });
    }

    base.push(
      {
        accessorKey: 'night_time',
        header: t('logbook.night'),
        meta: { width: 62, align: 'right', mono: true },
        cell: ({ row }) => nonZero(row.original.night_time),
      },
      {
        accessorKey: 'ifr_time',
        header: t('logbook.ifr'),
        meta: { width: 62, align: 'right', mono: true },
        cell: ({ row }) => nonZero(row.original.ifr_time),
      },
    );

    if (showMyColumns) {
      base.push({
        id: 'my_function',
        header: t('logbook.function'),
        meta: { width: 110 },
        cell: ({ row }) =>
          row.original.my_function ? (
            <Group gap={6} wrap="nowrap">
              <Badge size="xs" variant="light" color={crewColor(row.original.my_function)}>
                {enumLabel('crew_function', row.original.my_function)}
              </Badge>
              <Text fz="xs" ff="monospace">
                {row.original.my_time}
              </Text>
            </Group>
          ) : null,
      });
    } else {
      base.push({
        id: 'crew',
        header: t('logbook.crew'),
        meta: { width: 180 },
        cell: ({ row }) => (
          <Group gap={4} wrap="nowrap">
            {row.original.crew_summary
              .filter((crew) => crew.function !== 'PIC')
              .slice(0, 3)
              .map((crew, index) => (
                <Tooltip key={index} label={enumLabel('crew_function', crew.function)} withArrow>
                  <Badge size="xs" variant="light" color={crewColor(crew.function)}>
                    {crew.name}
                  </Badge>
                </Tooltip>
              ))}
          </Group>
        ),
      });
    }

    base.push(
      {
        accessorKey: 'purpose',
        header: t('logbook.purpose'),
        meta: { width: 120 },
        cell: ({ row }) => (
          <Text fz="xs" c="dimmed">
            {purposeLabel(t, row.original.purpose ?? '', row.original.purpose_label)}
          </Text>
        ),
      },
      {
        accessorKey: 'remarks',
        header: t('common.remarks'),
        cell: ({ row }) => (
          <Text fz="xs" c="dimmed" lineClamp={1} title={row.original.remarks ?? ''}>
            {row.original.remarks}
          </Text>
        ),
      },
      {
        accessorKey: 'state',
        header: '',
        meta: { width: 40 },
        cell: ({ row }) =>
          row.original.state === 'CERTIFIED' ? (
            <Tooltip label={t('logbook.CERTIFIED')} withArrow>
              <IconSignature size={15} opacity={0.5} />
            </Tooltip>
          ) : (
            <StatusBadge kind="flight" value={row.original.state} size="xs" variant="light" />
          ),
      },
    );

    return base;
  }, [t, enumLabel, kind, isGliding, showMyColumns]);

  const exportBook = async (format: 'pdf' | 'xlsx') => {
    const params = new URLSearchParams({ export: format });
    if (query.date_from) params.set('date_from', query.date_from);
    if (query.date_to) params.set('date_to', query.date_to);
    if (query.purpose) params.set('purpose', query.purpose);
    try {
      await downloadFile(
        `/logbook/view/${kind}/${subjectId}/?${params.toString()}`,
        `logbook.${format === 'pdf' ? 'pdf' : 'xlsx'}`,
      );
    } catch (error) {
      notifyError(error);
    }
  };

  const summary = logbook.data?.summary;
  const pageSummary = logbook.data?.page_summary;

  return (
    <Stack gap="sm">
      <Group justify="space-between" align="flex-end" wrap="wrap" gap="sm">
        <Group gap="sm" wrap="wrap">
          <DatePickerInput
            placeholder={t('common.from')}
            value={dateFrom}
            onChange={(value) => {
              setDateFrom(value as Date | null);
              setPage(1);
            }}
            clearable
            w={140}
            valueFormat="DD.MM.YYYY"
          />
          <DatePickerInput
            placeholder={t('common.to')}
            value={dateTo}
            onChange={(value) => {
              setDateTo(value as Date | null);
              setPage(1);
            }}
            clearable
            w={140}
            valueFormat="DD.MM.YYYY"
          />
          <Select
            placeholder={t('logbook.purpose')}
            data={enumChoices('flight_purpose').map((choice) => ({
              value: choice.value,
              label: purposeLabel(t, choice.value, choice.label),
            }))}
            value={purpose}
            onChange={(value) => {
              setPurpose(value);
              setPage(1);
            }}
            clearable
            w={170}
          />
          <TextInput
            placeholder={t('action.search')}
            leftSection={<IconSearch size={15} />}
            value={search}
            onChange={(event) => {
              setSearch(event.currentTarget.value);
              setPage(1);
            }}
            w={180}
          />
          <Button
            variant={onlyUncertified ? 'filled' : 'default'}
            size="sm"
            onClick={() => {
              setOnlyUncertified((value) => !value);
              setPage(1);
            }}
          >
            {t('logbook.uncertifiedOnly')}
          </Button>
        </Group>

        <Group gap="xs">
          <Menu position="bottom-end">
            <Menu.Target>
              <ActionIcon variant="default" size="lg" aria-label={t('action.export')}>
                <IconDownload size={17} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Label>{t('action.export')}</Menu.Label>
              <Menu.Item
                leftSection={<IconFileTypePdf size={15} />}
                onClick={() => void exportBook('pdf')}
              >
                {t('action.exportPdf')}
              </Menu.Item>
              <Menu.Item
                leftSection={<IconFileSpreadsheet size={15} />}
                onClick={() => void exportBook('xlsx')}
              >
                {t('action.exportXlsx')}
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
          {can('logbook.add') ? (
            <Button
              leftSection={<IconPlus size={16} />}
              onClick={() => {
                setFlightId(null);
                setDrawerOpen(true);
              }}
            >
              {t('logbook.fileFlight')}
            </Button>
          ) : null}
        </Group>
      </Group>

      {summary ? <SummaryStrip summary={summary} title={title ?? logbook.data?.title} /> : null}

      <DataTable
        data={logbook.data?.results ?? []}
        columns={columns}
        isLoading={logbook.isLoading}
        onRowClick={(row) => {
          setFlightId(row.id);
          setDrawerOpen(true);
        }}
        rowProps={(row) => (row.is_cancelled ? { opacity: 0.45 } : {})}
        emptyTitle={t('logbook.noFlights')}
        emptyHint={t('logbook.noFlightsHint')}
        minWidth={1280}
        pagination={{
          page,
          pageSize,
          total: logbook.data?.count ?? 0,
          onPageChange: setPage,
          onPageSizeChange: (size) => {
            setPageSize(size);
            setPage(1);
          },
        }}
        footer={
          pageSummary ? (
            <Table.Tr>
              <Table.Td colSpan={columns.length}>
                <Group gap="lg" wrap="wrap">
                  <Text fz="xs" c="dimmed" tt="uppercase" fw={600}>
                    {t('logbook.thisPage')}
                  </Text>
                  <Text fz="sm" ff="monospace" fw={600}>
                    {pageSummary.total_display}
                  </Text>
                  <Text fz="xs" c="dimmed">
                    {t('logbook.landings')}: {pageSummary.landings_total}
                  </Text>
                  {pageSummary.launches ? (
                    <Text fz="xs" c="dimmed">
                      {t('logbook.launches')}: {pageSummary.launches}
                    </Text>
                  ) : null}
                </Group>
              </Table.Td>
            </Table.Tr>
          ) : null
        }
      />

      <FlightDrawer
        opened={isDrawerOpen}
        onClose={() => setDrawerOpen(false)}
        flightId={flightId}
        defaultAircraftId={aircraftId}
        defaultPersonId={personId}
      />
    </Stack>
  );
}

/** Hides a zero so the eye lands on the columns that actually carry time. */
function nonZero(value?: string | null) {
  return value && value !== '0:00' ? value : '';
}

/** Instructor, examiner and student rows are the ones a reader scans a page for. */
function crewColor(fn?: string | null) {
  if (fn === 'INSTRUCTOR') return 'grape';
  if (fn === 'EXAMINER') return 'orange';
  if (fn === 'STUDENT' || fn === 'SPIC') return 'blue';
  if (fn === 'PASSENGER' || fn === 'OCCUPANT') return 'gray';
  return 'teal';
}

function SummaryStrip({ summary, title }: { summary: LogbookSummary; title?: string }) {
  const { t } = useTranslation();
  const cells: [string, string | number][] = [
    [t('logbook.flights'), summary.flights],
    [t('logbook.totalTime'), summary.total_display],
    [t('logbook.landings'), summary.landings_total],
  ];
  if (summary.launches) cells.push([t('logbook.launches'), summary.launches]);
  if (summary.night_minutes) cells.push([t('logbook.night'), minutesToHhmm(summary.night_minutes)]);
  if (summary.ifr_minutes) cells.push([t('logbook.ifr'), minutesToHhmm(summary.ifr_minutes)]);
  if (summary.pic_minutes) cells.push([t('logbook.pic'), minutesToHhmm(summary.pic_minutes)]);
  if (summary.dual_minutes) cells.push([t('logbook.dual'), minutesToHhmm(summary.dual_minutes)]);
  if (summary.instructor_minutes) {
    cells.push([t('logbook.instructorTime'), minutesToHhmm(summary.instructor_minutes)]);
  }

  return (
    <Group gap="xl" wrap="wrap">
      {title ? (
        <Text fz="sm" fw={600}>
          {title}
        </Text>
      ) : null}
      {cells.map(([label, value]) => (
        <Stack key={label} gap={0}>
          <Text fz={10} c="dimmed" tt="uppercase" fw={600}>
            {label}
          </Text>
          <Text fz="sm" fw={600} ff="monospace">
            {value}
          </Text>
        </Stack>
      ))}
    </Group>
  );
}
